import { create } from 'zustand'
import type { AnyAnswer } from '../../api/types.ts'
import { forgetLastName } from './lastName.ts'

/**
 * What this device holds for the link it is on: the attempt it is working in and the answers typed
 * so far, until they reach the server. Nothing about a result. The server owns that, and gives it
 * back to this device's key (`lib/deviceKey.ts`), so there is no copy here to lose or to go stale.
 */
type Held = {
	attemptId: string | null
	learnerName: string
	answers: Record<string, AnyAnswer>
	/** Typed but not yet accepted by the server. */
	unsent: Record<string, true>
	/** The PUT never arrived (or got a gateway error): it is retried. */
	failed: Record<string, true>
	/**
	 * Answers the server turned away for good: no longer owed (not in `unsent`), never retried, and they
	 * do not hold back the hand-in. Kept with the answers, so a reload still shows which did not go and
	 * the hand-in still asks about them as blanks. A new answer for the question clears it. A store
	 * written before this field existed reads as none.
	 */
	refused: Record<string, Refusal>
	/** She handed this in: the page asks the server for the result instead of showing a form. */
	handedIn: boolean
	/** True when the server handed back an attempt she had already started. */
	resumed: boolean
}

/**
 * Why the server turned an answer away for good, as opposed to a connection that failed:
 * `closed` the attempt is already handed in; `refused` any other refusal (the question is gone,
 * the answer was not accepted).
 */
export type Refusal = 'closed' | 'refused'

export type AttemptState = Held & {
	/** The link the page is on. Every other field is what this device holds for it. */
	slug: string | null
	/**
	 * The hand-in is under way (sending the last answers, then the server marking): the answers are
	 * what is being marked, so they cannot change. Not kept: a reload starts with the form open.
	 */
	submitting: boolean
	setSubmitting: (on: boolean) => void

	begin: (o: {
		slug: string
		attemptId: string
		learnerName: string
		saved?: { exerciseId: string; answer: AnyAnswer | null }[]
		resumed?: boolean
		handedIn?: boolean
	}) => void
	answer: (exerciseId: string, answer: AnyAnswer) => void
	markSent: (exerciseId: string) => void
	markFailed: (exerciseId: string) => void
	/** The server will not take this answer: it is no longer owed, and no longer failing. */
	markRefused: (exerciseId: string, why: Refusal) => void
	/** She handed in: the form's answers are done with, the server has them. */
	finish: () => void
	/** The page is on this link: hold what this device has kept for it, nothing of another link's. */
	reset: (slug: string) => void
	/** Another tab changed what is kept for this link: take it. */
	reload: () => void
	/**
	 * "Not you?": show the name gate to the next student and forget her name. Her attempt and its
	 * result stay on the server and come back to her key, and her other links are untouched.
	 */
	restart: () => void
}

const none: Held = {
	attemptId: null,
	learnerName: '',
	answers: {},
	unsent: {},
	failed: {},
	refused: {},
	handedIn: false,
	resumed: false,
}

/**
 * Persisted to localStorage, not sessionStorage: a phone drops a backgrounded tab routinely, and
 * losing the attempt there meant the name gate and a second attempt for the same learner. One entry
 * per link, so opening another link never touches this one; the 30 most recent are kept.
 */
const STORAGE_KEY = 'tt-attempt'
const KEEP_LINKS = 30
/**
 * Bump when the stored shape changes. A persisted answer whose shape no longer matches its exercise
 * kind would throw inside a render, so old state is dropped rather than migrated.
 * 4: per link, and no result kept (3 was one slot with the marked result in it).
 */
const VERSION = 4

type Links = Record<string, Held>

function readLinks(): { raw: string | null; links: Links } {
	try {
		const raw = localStorage.getItem(STORAGE_KEY)
		const parsed = raw ? (JSON.parse(raw) as { v?: number; links?: Links }) : null
		return { raw, links: parsed?.v === VERSION && parsed.links ? parsed.links : {} }
	} catch {
		return { raw: null, links: {} }
	}
}

const heldOf = (s: Held): Held => ({
	attemptId: s.attemptId,
	learnerName: s.learnerName,
	answers: s.answers,
	unsent: s.unsent,
	failed: s.failed,
	refused: s.refused,
	handedIn: s.handedIn,
	resumed: s.resumed,
})

const load = (slug: string | null): Held => ({
	...none,
	...(slug ? readLinks().links[slug] : undefined),
})

/** Write-through: what the store holds for the current link, kept under that link alone. */
function save(s: AttemptState) {
	if (!s.slug) return
	const { raw, links } = readLinks()
	const { [s.slug]: _old, ...rest } = links
	// Most recent last, so the cap drops the oldest.
	const next = s.attemptId ? { ...rest, [s.slug]: heldOf(s) } : rest
	const text = JSON.stringify({
		v: VERSION,
		links: Object.fromEntries(Object.entries(next).slice(-KEEP_LINKS)),
	})
	// Unchanged: a write would wake the other tabs, which would write back, and so on.
	if (text === raw) return
	try {
		localStorage.setItem(STORAGE_KEY, text)
	} catch {}
}

export const useAttemptStore = create<AttemptState>()((set, get) => ({
	...none,
	slug: null,
	submitting: false,
	setSubmitting: (submitting) => set({ submitting }),

	begin: ({ slug, attemptId, learnerName, saved = [], resumed = false, handedIn = false }) =>
		set({
			slug,
			attemptId,
			learnerName,
			// Seeded from the server's copy of a resumed attempt. This is not a duplicate of
			// query state: these rows are the learner's own in-progress work, and the page
			// owns them from here on.
			answers: Object.fromEntries(
				saved.flatMap((r) => (r.answer === null ? [] : [[r.exerciseId, r.answer] as const])),
			),
			unsent: {},
			failed: {},
			refused: {},
			handedIn,
			resumed,
		}),

	answer: (exerciseId, answer) =>
		set((s) => {
			const { [exerciseId]: _dropped, ...failed } = s.failed
			// A new answer is a new chance: whatever turned the last one away is asked again.
			const { [exerciseId]: _refused, ...refused } = s.refused
			return {
				answers: { ...s.answers, [exerciseId]: answer },
				unsent: { ...s.unsent, [exerciseId]: true },
				failed,
				refused,
			}
		}),

	markSent: (exerciseId) =>
		set((s) => {
			const { [exerciseId]: _u, ...unsent } = s.unsent
			const { [exerciseId]: _f, ...failed } = s.failed
			const { [exerciseId]: _r, ...refused } = s.refused
			return { unsent, failed, refused }
		}),

	markFailed: (exerciseId) => set((s) => ({ failed: { ...s.failed, [exerciseId]: true } })),

	markRefused: (exerciseId, why) =>
		set((s) => {
			const { [exerciseId]: _u, ...unsent } = s.unsent
			const { [exerciseId]: _f, ...failed } = s.failed
			return { unsent, failed, refused: { ...s.refused, [exerciseId]: why } }
		}),

	finish: () =>
		set({
			handedIn: true,
			resumed: false,
			answers: {},
			unsent: {},
			failed: {},
			refused: {},
			submitting: false,
		}),

	restart: () => {
		// Before the gate remounts, or it would prefill the name she is not.
		forgetLastName()
		set(none)
	},

	reset: (slug) => {
		if (get().slug === slug) return
		set({ slug, ...load(slug) })
	},

	reload: () => {
		const held = load(get().slug)
		if (JSON.stringify(held) !== JSON.stringify(heldOf(get()))) set(held)
	},
}))

useAttemptStore.subscribe(save)

export const selectAnswer = (id: string) => (s: AttemptState) => s.answers[id]
export const selectUnsentCount = (s: AttemptState) => Object.keys(s.unsent).length
export const selectFailedCount = (s: AttemptState) => Object.keys(s.failed).length
export const selectRefusal = (id: string) => (s: AttemptState) => s.refused[id]
export const selectIsFailed = (id: string) => (s: AttemptState) => !!s.failed[id]

/**
 * What the quiet line under the bar may claim. "Saved" is only true when nothing is waiting, failed or
 * turned away: a refused answer counts as blank, so the line says nothing and the red note speaks.
 */
export function saveStateOf(c: {
	unsent: number
	failed: number
	refused: number
	online: boolean
}): 'saving' | 'saved' | null {
	if (c.failed > 0 || !c.online) return null
	if (c.unsent > 0) return 'saving'
	return c.refused > 0 ? null : 'saved'
}
